import styled from 'styled-components';
import {
  TextField,
  MenuItem as MUIMenuItem,
  ListItem as MUIListItem,
  ListItemIcon as MUIListItemIcon,
  Button,
  Box,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/RemoveCircle';
import { colors } from 'spw-theme';
import {
  JustifyStartRow,
  JustifyCenterAlignEndColumn,
} from 'spw-shared/globalComponents/grid';
import MUIAddCircle from '@mui/icons-material/AddCircle';
import { DraggingStyle, NotDraggingStyle } from 'react-beautiful-dnd';
import { GridColumn } from 'spw-programming-projects/building-details/worksheet/styles';

interface ColorDotProps {
  color: string;
}

export const ColorDot = styled('div')<ColorDotProps>`
  background: ${({ color }) => color} !important;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    font-size: 16px;
  }

  &.add-margin {
    margin-left: 8px;
  }

  &.add-margin-right {
    margin-right: 8px;
  }

  &.large-button {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    cursor: pointer;
  }
`;

export const SmallColorDot = styled.div`
  && {
    width: 12px;
    height: 12px;
    border-radius: 9px;
    background-color: ${(props) => props.color} !important;
  }
`;

export const ActionIcon = styled.div`
  .colorDot {
    margin-left: 0;
    margin-right: 10px;
  }
`;

export const ActionLabel = styled.div`
  .spaceTypeSubTitle {
    font-family: Roboto;
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    color: ${colors.black};
    text-transform: uppercase;
  }

  .spaceTypeCount {
    margin-left: 34px;
  }
`;

export const RowSpaceTypeLabel = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${colors.black};
  text-align: left;
  white-space: nowrap;
`;

export const DialogContentHeader = styled.div`
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  text-transform: ${(props: { $textTransform?: string }) =>
    props.$textTransform || 'uppercase'};
  color: ${colors.black};
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: 12px;
`;

export const DialogContentText = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.black};
`;

export const MutedDialogContentText = styled(DialogContentText)`
  color: #919191;
  margin-top: 9px;
`;

export const ActionContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-left: 8px;

  width: 100%;
`;

// NOTE: the !important(s) in the next block are ABSOLUTELY REQUIRED
// for the download, otherwise the emulator overrides the background color with transparent

export const GridHeaderColumn = styled(GridColumn)`
  && {
    height: 40px;
    font-weight: bold;
    font-size: 10px;
    line-height: 12px;
    padding-bottom: 8px;
    padding-right: 12px;
    justify-content: flex-end;
    align-items: flex-end;
    text-align: right;
  }

  &.justify-start {
    justify-content: flex-start;
  }

  &.lighter {
    background-color: ${colors.selectBorder};
  }

  &.row-end {
    padding-right: 16px;
  }

  &.seat-percent-column {
    padding-right: 48px;
  }

  &.nowrap {
    white-space: nowrap;
  }
`;

export const SpaceTypeNameContainer = styled(JustifyStartRow)<{
  $disablePointer?: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  padding: 4px 8px;
  border: 1px solid transparent;
  height: 40px;
  border-radius: 4px;
  ${({ $disablePointer }) =>
    $disablePointer ? 'cursor: default;' : 'cursor: pointer;'}

  &.hover {
    border: 1px solid ${colors.fontColor};
  }
`;

export const WorksheetInput = styled(TextField)`
  && {
    background-color: ${colors.white};
    border-radius: 4px;
    width: 100%;
    height: 40px;

    div.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${colors.containerBorder};
      border-width: 1px;
    }

    div.MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-formControl {
      height: 40px;
      font-size: 14px;
      line-height: 16px;
      letter-spacing: 0.25px;
    }

    div.MuiOutlinedInput-adornedEnd {
      padding-right: 8px;
    }

    div.MuiOutlinedInput-adornedStart {
      padding-left: 8px;
    }

    input {
      text-align: right;
      font-size: 14px;
      padding: 18.5px 6px;
    }

    .MuiOutlinedInput-multiline {
      padding: 4px 4px;
    }

    .MuiOutlinedInput-inputMultiline {
      padding: 4px 5px;
      overflow: hidden;
      font-size: 14px;
      letter-spacing: normal;
    }

    textarea {
      overflow: hidden;
    }

    &.left-align {
      input {
        text-align: left;
      }
    }

    &.padding-left-0 {
      .MuiOutlinedInput-adornedStart {
        padding-left: 8px;
      }

      input {
        padding-left: 0;
      }
    }

    &.duplicated {
      fieldset {
        border-color: ${colors.red};
      }

      .Mui-focused fieldset {
        border-color: ${colors.red};
      }

      input,
      textarea {
        color: ${colors.red};
      }
    }

    &.full-width {
      width: 116px;
      height: 32px;

      div {
        width: 116px;
        height: 32px;

        input {
          padding: 9px 12px;
          width: 100%;
          text-align: left;
          background-color: white;
        }
      }
    }

    &.width60 {
      width: 60px;

      input {
        padding: 18.5px 6px;
      }
    }
  }

  &.usf-input {
    input {
      padding-left: 5px;
      padding-right: 10px;
    }
  }
`;

export const ArrowContainer = styled.div`
  cursor: pointer;

  img {
    margin-right: 4px;
    margin-left: 2px;
  }
`;

export const Container = styled.div`
  width: 1090px;
  display: flex;
  flex-direction: column;
  border-radius: 9px 9px 0px 0px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.25);
  position: fixed;
  bottom: -8px;
  right: 20px;
  z-index: 1000; //MINIMUM ZINDEX TO BE ON TOP OF THE FLOORPLAN
  background: white;

  .MuiTableCell-stickyHeader {
    background: white;
  }

  @media (min-width: 1440px) {
    right: calc(50% - 770px + 50px + 24px);
  }
`;

export const Content = styled.div`
  overflow-y: scroll;
  padding-bottom: 126px;
`;

export const Header = styled.div`
  min-height: 40px;
  background: ${colors.mainGray};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: grab;

  span {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    margin-left: 16px;
    color: ${colors.white};
  }
`;

export const Subtitles = styled.div`
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.25px;
  text-align: right;
  color: ${colors.fontColor};
  cursor: default;

  &.nowrap {
    white-space: nowrap;
  }

  .unused-spaces-list-trigger {
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    text-wrap: nowrap;
    padding: 6px 28px 7px;
    background: transparent;
    border: 0;
    color: unset;

    &:not(:disabled) {
      cursor: pointer;
    }
  }
  .error {
    color: ${colors.red};
  }
`;

export const HeaderRow = styled('div')<{ margin?: string }>`
  color: ${colors.black};
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: right;
  text-transform: uppercase;
  margin: ${(props) => props.margin};
  cursor: default;
`;

export const TableRowCustom = styled('div')<{ rowHeight?: number }>`
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  border-bottom: 1px solid ${colors.containerBorder};
  height: ${(props) => props.rowHeight || 65}px;
`;

export const ColumnItem = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;

  button {
    width: 30px;
    margin: 0 auto;
  }
`;

export const CategorySubHeader = styled('span')<{ marginLeft?: string }>`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  margin-left: ${(props) => props.marginLeft};
  color: ${colors.black};
  white-space: nowrap;
`;

export const DialogHeader = styled.span`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  color: ${colors.black};
`;

export const MultiplierBox = styled.div`
  && {
    position: relative;
    right: calc(-8px - ((100% - 60px) / 2));
  }
`;

export const MenuItem = styled(MUIMenuItem)`
  &.empty-list-message.MuiListItem-button:hover {
    background-color: unset;
    cursor: default;
  }

  .dot {
    width: 16px;
    height: 16px;
    border-radius: 8px;
    margin-right: 8px;
  }

  && {
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    width: 100%;

    .child {
      padding-left: 24px;
      margin-top: 8px;
    }

    .present {
      color: ${colors.greyInputAddornment};
    }
  }

  &.title {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: ${colors.black};
    text-transform: uppercase;
  }

  &.title:hover {
    background-color: ${colors.white};
  }

  &.child {
    padding-left: 40px;
  }

  &.present {
    color: ${colors.greyInputAddornment};
  }
`;

export const ListItem = styled(MUIListItem)`
  && {
    height: 56px;
    background-color: ${colors.white};
    display: flex;
    justify-content: space-between;
    padding: 18px 24px;
    border-bottom: 1px solid ${colors.containerBorder};
  }

  &.selected {
    background-color: ${colors.backgroundGrey};
  }

  &&:hover {
    cursor: default;
  }

  &.disabled {
    color: ${colors.emptyField};

    svg {
      ${colors.black};
    }

    span.italic {
      font-style: italic;
    }
  }

  &.disabled:hover {
    cursor: default;
  }
`;

export const ListItemIcon = styled(MUIListItemIcon)`
  && {
    min-width: 0px;
  }
`;

export const HeaderValue = styled.div`
  && {
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 16px;
    /* or 114% */
    letter-spacing: 0.25px;

    /* Text/Default */

    color: ${colors.black};
  }
`;

export const ProgramWorksheetJustifyCenterAlignEndColumn = styled(
  JustifyCenterAlignEndColumn
)`
  && {
    height: 44px;
  }
`;

export const Padded = styled.div`
  && {
    padding: 4px 0;
  }
`;

export const PaddedNoOverflow = styled.div`
  && {
    padding: 8px 0;
    white-space: nowrap;
  }
`;

export const EditRoundingSettingsContainer = styled(Box)`
  position: absolute;
  right: 0px;
  top: 4px;
`;
export const WorkSeatsHoverArea = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: -30px;
`;

export const WorkSeatsRoundingSettingsModalContainer = styled(Box)`
  padding: 12px 0px;
  font-size: 12px;
  min-width: 112px;
  z-index: 1;
  background-color: white;
  .work-seats {
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
    margin-left: 12px;
  }
  && {
    .MuiMenuItem-root {
      font-size: 12px;
    }
  }
`;

export const NoOverflow = styled.div`
  && {
    white-space: nowrap;
  }
`;

export const PaddedIconBox = styled.div`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
`;

export const AddCircle = styled(MUIAddCircle)`
  && {
    color: #00819e;
  }
`;

export const MacroMenuItem = styled(MenuItem)`
  && {
    font-family: Roboto;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    padding: 9px 24px 9px 24px;

    &.title {
      font-weight: bold;
      font-size: 10px;
      letter-spacing: 0.25px;
      text-transform: uppercase;
      color: #919191;
      opacity: unset;
      padding-bottom: 7px;
      padding-top: 8px;
    }

    &.bold-title {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.25px;
      color: ${colors.black};
      opacity: initial;
    }
  }
`;

export const NotesButton = styled(Button)`
  && {
    font-size: 14px;
    text-transform: uppercase;
    border-left: 1px solid ${colors.greyInputAddornment};
    border-right: 1px solid ${colors.greyInputAddornment};
    padding: 0px 37.5px;
    border-radius: 0px;
    height: 36px;

    .MuiButton-label {
      margin-left: 0;

      .MuiButton-startIcon {
        margin-left: 0;
      }
    }

    span {
      font-size: 14px;
      margin-left: 0px;
    }

    &.Mui-disabled {
      opacity: 0.75;
    }

    span,
    svg {
      color: ${colors.white} !important;
    }
  }
`;

export const TipsButton = styled(Button)`
  && {
    font-weight: 500;
    font-size: 14px;
    border-left: 1px solid #919191;
    padding: 0px 42.5px;
    border-radius: 0px;
    height: 36px;

    .MuiButton-label {
      margin-left: 0;

      .MuiButton-startIcon {
        margin-left: 0;
      }
    }

    span {
      font-size: 14px;
      margin-left: 0px;
    }

    &.Mui-disabled {
      opacity: 0.75;
    }

    span,
    svg {
      color: ${colors.white} !important;
    }
  }
`;

export const SubtotalTooltipTitle = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 20px;
  margin-bottom: 16px;
  color: ${colors.black};
`;

export const SubtotalTooltipText = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.black};

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

export const DraggableTooltipContainer = styled.div`
  width: 298px;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0px 2px 25px rgba(0, 0, 0, 0.21);
  border-radius: 4px;

  & > .title {
    cursor: move;
    padding: 16px 16px;
    margin: 0;
  }

  & > .content {
    padding: 0 16px 20px;
  }
`;

export const SubtotalTooltipContainer = styled.div`
  width: 298px;
  box-sizing: border-box;
  padding: 16px 16px 20px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0px 2px 25px rgba(0, 0, 0, 0.21);
  border-radius: 4px;
`;

export const RemoveChildIcon = styled(RemoveIcon)`
  && {
    color: ${colors.mainGray};
    cursor: pointer;
    font-size: 18px;
  }
`;

export const getItemStyle = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle
) => ({
  background: isDragging ? '#EEEEEE' : 'white',
  // styles we need to apply on draggables
  ...draggableStyle,
});

export const getListStyle = (isDraggingOver: boolean) => ({
  background: isDraggingOver ? 'rgba(3,178,171, 0.1)' : 'white',
  width: '100%',
});

export const WorksheetHeaderIcon = styled.img`
  && {
    width: 32px;
    height: 32px;
  }
`;

export const UnusedSpacePopupHeader = styled.div`
  padding: 8px 16px;
  border-bottom: 1px solid ${colors.containerBorder};
  box-sizing: border-box;
  min-width: 227px;
  margin-bottom: 4px;
  cursor: move;
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
`;

export const UnusedSpacesPopUpTitle = styled.h5`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.black};
  margin-top: 0px;
  margin-bottom: 0px;
`;

export const UnusedSpacesPopUpSubtitle = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${colors.fontColor};
  margin-top: 0px;
  margin-bottom: 0px;
`;

export const UnusedSpaceTypesListTriggerWrapper = styled.div<{
  $disablePointer: boolean;
}>`
  padding: 0 8px;
  border-radius: 8px;
  border: 1px solid ${colors.containerBorder};
  height: 56px;
  box-sizing: border-box;
  ${({ $disablePointer }) =>
    $disablePointer ? 'cursor: default;' : 'cursor: pointer;'}
  display: flex;
  align-items: center;

  &:hover {
    ${({ $disablePointer }) =>
      $disablePointer
        ? 'box-shadow: none;'
        : 'box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);'}
  }

  .texts {
    display: flex;
    flex-direction: column;
    text-align: left;
  }

  .title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    white-space: nowrap;
    ${({ $disablePointer }) => ($disablePointer ? 'cursor: default;' : '')}
  }

  .color-dot {
    margin-right: 8px;
    ${({ $disablePointer }) => ($disablePointer ? 'cursor: default;' : '')}
  }
`;
